// Reads the current user's monthly credit usage + tier budget from the
// `check-credit-limit` edge function. Surfaces helpers the Hub can use to
// gate AI calls and trigger the upgrade modal.
//
// Refetched on auth change and when callers explicitly call `refresh()`
// (e.g. after a Hub action so the next render reflects the new total).
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { PlanId } from "@/lib/plans";

export type CreditLimit = {
  allowed: boolean;
  warn: boolean;
  used: number;
  /** `null` = unlimited (Zero-Day tier). */
  limit: number | null;
  plan: PlanId;
  nextTier: PlanId | null;
};

const DEFAULT: CreditLimit = {
  allowed: true,
  warn: false,
  used: 0,
  limit: null,
  plan: "script_kiddie",
  nextTier: "operator",
};

export const useCreditLimit = () => {
  const { user } = useAuth();
  const [data, setData] = useState<CreditLimit | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const { data: resp, error } = await supabase.functions.invoke(
        "check-credit-limit",
      );
      if (error) {
        console.error("check-credit-limit failed", error);
        setData(DEFAULT);
        return;
      }
      setData({
        allowed: !!resp?.allowed,
        warn: !!resp?.warn,
        used: typeof resp?.used === "number" ? resp.used : 0,
        limit: typeof resp?.limit === "number" ? resp.limit : null,
        plan: (resp?.plan ?? "script_kiddie") as PlanId,
        nextTier: (resp?.nextTier ?? null) as PlanId | null,
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /** Percentage of monthly budget consumed (0–1). Returns 0 for unlimited. */
  const pct = data?.limit ? Math.min(1, data.used / data.limit) : 0;

  return { data, loading, refresh, pct };
};
